import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { importProvidersFrom, inject } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routes';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideCopilotKit } from '@copilotkit/angular';
import { apiDedupInterceptor } from './app/interceptors/api-dedup.interceptor';
import { sessionStatusInterceptor } from './app/interceptors/session-status.interceptor';
import { SESSION_HEARTBEAT } from './app/service/session-idle-warning.service';
import { SessionHeartbeatService } from './app/service/session-heartbeat.service';
import { ApiEffects } from './app/state-files/effects';
import { apiReducer } from './app/state-files/reducer';
import { environment } from './app/environments/environment';
import { buildInfo } from './app/environments/build-info';

/**
 * Loads `assets/i18n/<lang>.json`, versioned with the build's commit hash
 * (`?v=<hash>`, stamped by set-version.js before every build). The files are
 * served from the same URL in every release, so without the version a browser
 * or proxy can keep a cached copy from the previous deploy, and keys added in
 * the new release render as their raw names.
 * @param http - Angular HTTP client used by the loader.
 * @returns The translation loader.
 */
export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, 'assets/i18n/', `.json?v=${buildInfo.hash}`);
}

// Syncfusion licensing is handled at BUILD time (`npx syncfusion-license
// activate`, run with the SYNCFUSION_LICENSE env var in the Dockerfile build
// stage and the CI workflows), which patches the installed @syncfusion packages
// so no license key ships in the browser bundle. Do NOT reintroduce
// `registerLicense()` here -- a key registered in code is readable in DevTools.
// Note: the key is tied to the @syncfusion/* MAJOR version (currently 34.1.29);
// regenerate the SYNCFUSION_LICENSE secret/env value whenever the packages
// cross a major, or components render an "invalid license" banner.

bootstrapApplication(AppComponent, {
  providers: [
    provideAnimations(),
    provideRouter(appRoutes),
    // sessionStatusInterceptor sits closest to the network, so it sees each real request's
    // 401 exactly once (a dedup-coalesced request is one network call). It only observes
    // errors and rethrows them unchanged; it also feeds SessionActivityService from successful non-passive calls.
    provideHttpClient(withInterceptors([apiDedupInterceptor, sessionStatusInterceptor])),
    // "Stay signed in" heartbeat: POST /token/session-heartbeat through ApiService (unapproved server contract).
    { provide: SESSION_HEARTBEAT, useFactory: () => { const heartbeat = inject(SessionHeartbeatService); return () => heartbeat.beat(); } },
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      })
    ),
    provideStore({ api: apiReducer}),
    provideEffects([ApiEffects]),
    ...(environment.production ? [] : [provideStoreDevtools({ maxAge: 25 })]),
    provideCopilotKit({
      // No `runtimeUrl` — the app does not use a CopilotKit runtime server.
      // Real agents backed by the Supervisor Agent WebSocket (AI_EXTERNAL_APIS)
      // are registered at runtime from AiDrawerComponent, which owns the
      // injected AiTokenService / RegaiHelpaIChatService instances.
      //
      // The license key now comes from `POST /common/assistant-config`, which is
      // authenticated and therefore cannot be called this early, so no key is
      // seeded at bootstrap. AiDrawerComponent.applyCopilotLicense patches the
      // real key in once the config loads. Nothing paints or logs a watermark
      // in the meantime at the installed @copilotkit/angular version
      // (LICENSE_WATERMARK_ENABLED is compiled to false there) — re-check this
      // if that package is upgraded.
    })
  ],
}).catch((err) => console.error(err));
